import {
  CalendarDays,
  LibraryBig,
  MessagesSquare,
  Search,
  Sparkles,
  User,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  /** Key into the strings catalog (nav.*). */
  labelKey: string;
  icon: LucideIcon;
  /** Exact-match only (the home route). */
  end?: boolean;
  /** Render the Discussions unread badge (`useDiscussionsBadge`) on this item. */
  showUnreadBadge?: boolean;
  /** Which nav surfaces list it — both when omitted. */
  surfaces?: readonly ('rail' | 'tabs')[];
}

/**
 * The primary destinations, ordered by how often they are used (the approved
 * web redesign, "Navigation"): Tonight (home) · Library · Discover ·
 * Discussions (badge = unread replies and mentions) · Calendar. Calendar is a
 * rail-only destination; the mobile tab bar keeps five tabs and ends with
 * Profile instead (the rail reaches Profile through its account menu).
 * Notifications (follow events) are the bell beside the account menu on the
 * rail, and on Tonight's masthead on mobile. Icon mapping: ui-ux.md §5.
 */
export const NAV_ITEMS: NavItem[] = [
  { to: '/', labelKey: 'nav.tonight', icon: Sparkles, end: true },
  { to: '/library', labelKey: 'nav.library', icon: LibraryBig },
  { to: '/discover', labelKey: 'nav.discover', icon: Search },
  { to: '/discussions', labelKey: 'nav.discussions', icon: MessagesSquare, showUnreadBadge: true },
  { to: '/calendar', labelKey: 'nav.calendar', icon: CalendarDays, surfaces: ['rail'] },
  { to: '/profile', labelKey: 'nav.profile', icon: User, surfaces: ['tabs'] },
];

function on(surface: 'rail' | 'tabs') {
  return (item: NavItem) => !item.surfaces || item.surfaces.includes(surface);
}

/** Desktop/tablet rail: the five destinations. */
export const RAIL_ITEMS: NavItem[] = NAV_ITEMS.filter(on('rail'));

/** Mobile tab bar: Tonight · Library · Discover · Discussions · Profile. */
export const TAB_ITEMS: NavItem[] = NAV_ITEMS.filter(on('tabs'));

/** The badge count string, capped at `99+` (same convention as the app). */
export function formatUnreadBadge(count: number): string {
  return count > 99 ? '99+' : String(count);
}
